import { useState } from "react";
import { BadgePercent, ChevronDown, CreditCard, Loader2, MapPin, Receipt, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah } from "../lib/format";
import { useApp } from "../context/AppContext";
import { checkoutApi } from "../lib/api";

const CheckoutSummary = ({ className = "" }) => {
  const {
    cartItems,
    cartSubtotal,
    cartTotal,
    selectedCourier,
    selectedPaymentId,
    paymentMethods,
    shipping,
    couponCode,
    setCouponCode,
    appliedCoupon,
    setAppliedCoupon,
  } = useApp();
  const [open, setOpen] = useState(false);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const payment = paymentMethods.find((p) => p.code === selectedPaymentId);
  const shippingCost = Number(selectedCourier?.cost || 0);

  const hasAddress =
    shipping.name || shipping.address || shipping.kota || shipping.phone;

  const applyCoupon = async (event) => {
    event.preventDefault();
    if (!couponCode.trim()) return toast.error("Masukkan kode kupon terlebih dahulu");
    if (!selectedCourier) return toast.error("Pilih kurir dulu agar potongan ongkir bisa dihitung");

    try {
      setCheckingCoupon(true);
      const preview = await checkoutApi.previewDiscount({
        discount_code: couponCode.trim().toUpperCase(),
        shipping_cost: shippingCost,
        items: cartItems.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
        })),
      });
      setCouponCode(preview.code);
      setAppliedCoupon(preview);
      toast.success(`Kupon ${preview.code} diterapkan`);
    } catch (error) {
      setAppliedCoupon(null);
      toast.error(error.message || "Kupon tidak dapat digunakan");
    } finally {
      setCheckingCoupon(false);
    }
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  return (
    <aside
      className={`bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden ${className}`}
      data-testid="checkout-summary"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:hidden w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-stone-100"
        data-testid="summary-toggle"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center">
            <Receipt className="w-4 h-4" />
          </span>
          <div className="text-left">
            <p className="font-semibold text-stone-900">Ringkasan Pesanan</p>
            <p className="text-sm text-stone-500">
              {cartItems.length} produk - {formatRupiah(cartTotal || cartSubtotal)}
            </p>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div className={`px-5 sm:px-6 py-5 ${open ? "block" : "hidden"} md:block`}>
        <div className="hidden md:flex items-center gap-3 mb-4">
          <span className="inline-flex w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center">
            <Receipt className="w-5 h-5" />
          </span>
          <h3 className="font-display text-xl font-semibold text-stone-900">
            Ringkasan Pesanan
          </h3>
        </div>

        {cartItems.length === 0 ? (
          <p className="text-stone-500">Keranjang masih kosong.</p>
        ) : (
          <ul className="divide-y divide-stone-100 mb-3">
            {cartItems.map((item) => {
              const hasComparePrice = Number(item.comparePrice || 0) > Number(item.price || 0);
              const productDiscountPercent = hasComparePrice
                ? Math.round(((Number(item.comparePrice) - Number(item.price)) / Number(item.comparePrice)) * 100)
                : 0;

              return (
                <li key={item.productId} className="py-2.5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100 shrink-0">
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">
                      {item.productName}
                    </p>
                    <div className="mt-0.5 text-xs text-stone-500">
                      {hasComparePrice && (
                        <>
                          <p className="line-through">{formatRupiah(item.comparePrice)}</p>
                          <p className="font-medium text-rose-600">Diskon {productDiscountPercent}%</p>
                        </>
                      )}
                      <p>{formatRupiah(item.price)} x {item.quantity}</p>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-stone-900">
                    {formatRupiah(item.subtotal)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}

        <div className="space-y-2 border-t border-stone-100 pt-3 text-sm">
          <div className="flex justify-between text-stone-700">
            <span>Subtotal produk</span>
            <span className="font-medium text-stone-900">
              {formatRupiah(cartSubtotal)}
            </span>
          </div>
          <div className="flex justify-between text-stone-700">
            <span>Ongkir</span>
            <span className="font-medium text-stone-900">
              {selectedCourier ? formatRupiah(selectedCourier.cost) : "-"}
            </span>
          </div>
          {appliedCoupon?.product_discount_amount > 0 && (
            <div className="flex justify-between text-rose-700">
              <span>Potongan Produk ({appliedCoupon.code})</span>
              <span className="font-medium">-{formatRupiah(appliedCoupon.product_discount_amount)}</span>
            </div>
          )}
          {appliedCoupon?.shipping_discount_amount > 0 && (
            <div className="flex justify-between text-rose-700">
              <span>Potongan Ongkir ({appliedCoupon.code})</span>
              <span className="font-medium">-{formatRupiah(appliedCoupon.shipping_discount_amount)}</span>
            </div>
          )}
        </div>

        <form onSubmit={applyCoupon} className="mt-4 rounded-xl border border-stone-200 bg-stone-50 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-stone-900">
            <BadgePercent className="h-4 w-4 text-emerald-700" />
            Kupon Diskon
          </div>
          {appliedCoupon ? (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-stone-900">{appliedCoupon.code}</p>
                <p className="text-xs text-stone-500">
                  {appliedCoupon.discount_target === "shipping" ? "Potongan ongkir" : "Potongan produk"}
                </p>
              </div>
              <button
                type="button"
                onClick={clearCoupon}
                className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-900"
                aria-label="Hapus kupon"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                value={couponCode}
                onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                placeholder="Kode kupon"
                className="h-10 min-w-0 flex-1 rounded-lg border border-stone-200 bg-white px-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                disabled={checkingCoupon}
              />
              <button
                type="submit"
                disabled={checkingCoupon || !selectedCourier}
                className="inline-flex h-10 items-center rounded-lg bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:bg-stone-200 disabled:text-stone-400"
              >
                {checkingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pakai"}
              </button>
            </div>
          )}
        </form>

        <div className="mt-3 pt-3 border-t border-stone-200 flex justify-between items-baseline">
          <span className="font-display text-base font-medium text-stone-900">
            Total Bayar
          </span>
          <span
            className="font-display text-2xl font-semibold text-emerald-700"
            data-testid="summary-total"
          >
            {formatRupiah(cartTotal || cartSubtotal)}
          </span>
        </div>

        {(hasAddress || selectedCourier || payment) && (
          <div className="mt-4 space-y-3 text-sm">
            {hasAddress && (
              <div className="flex gap-3">
                <MapPin className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" />
                <div className="text-stone-700">
                  <p className="font-medium text-stone-900">
                    {shipping.name || "Alamat pengiriman"}
                  </p>
                  <p className="text-stone-500">
                    {shipping.phone && `${shipping.phone} - `}
                    {[shipping.address, shipping.kelurahan, shipping.kecamatan, shipping.kota]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
            )}
            {selectedCourier && (
              <div className="flex gap-3">
                <Truck className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" />
                <p className="text-stone-700">
                  {selectedCourier.name}
                  {selectedCourier.service ? ` - ${selectedCourier.service}` : ""}
                  {selectedCourier.etd ? ` - ${selectedCourier.etd}` : ""}
                </p>
              </div>
            )}
            {payment && (
              <div className="flex gap-3">
                <CreditCard className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" />
                <p className="text-stone-700">{payment.name}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default CheckoutSummary;
